// Minimal History-API router (path-based routes: /overview, /deals/2, ...).
// The static host serves index.html for every unknown path (nginx try_files).
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';

interface Loc {
  pathname: string;
  search: string;
}

interface RouterValue extends Loc {
  navigate: (to: string, opts?: { replace?: boolean }) => void;
}

const RouterCtx = createContext<RouterValue | null>(null);

function current(): Loc {
  return { pathname: window.location.pathname, search: window.location.search };
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [loc, setLoc] = useState<Loc>(current);

  useEffect(() => {
    const onPop = () => setLoc(current());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: string, opts?: { replace?: boolean }) => {
    if (opts?.replace) window.history.replaceState(null, '', to);
    else window.history.pushState(null, '', to);
    setLoc(current());
    window.scrollTo?.(0, 0);
  }, []);

  const value = useMemo(() => ({ ...loc, navigate }), [loc, navigate]);
  return <RouterCtx.Provider value={value}>{children}</RouterCtx.Provider>;
}

export function useRouter(): RouterValue {
  const v = useContext(RouterCtx);
  if (!v) throw new Error('useRouter must be used within a RouterProvider');
  return v;
}

export function useSearchParams(): URLSearchParams {
  const { search } = useRouter();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export function Link({ to, onClick, children, ...rest }: { to: string } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  const { navigate } = useRouter();
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  };
  return (
    <a href={to} onClick={handle} {...rest}>
      {children}
    </a>
  );
}

export type RouteMatch =
  | { name: 'overview' }
  | { name: 'mark' }
  | { name: 'deals' }
  | { name: 'deal'; id: number }
  | { name: 'entry' }
  | { name: 'loans' }
  | { name: 'closedloans' }
  | { name: 'events' }
  | { name: 'locations' }
  | { name: 'help' }
  | { name: 'redirect'; to: string }
  | { name: 'notfound' };

export function matchRoute(pathname: string): RouteMatch {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/') return { name: 'redirect', to: '/overview' };
  if (p === '/overview') return { name: 'overview' };
  if (p === '/mark') return { name: 'mark' };
  if (p === '/deals') return { name: 'deals' };
  const d = /^\/deals\/(\d+)$/.exec(p);
  if (d?.[1]) return { name: 'deal', id: Number(d[1]) };
  if (p === '/entries/new') return { name: 'entry' };
  if (p === '/loans') return { name: 'loans' };
  if (p === '/loans/closed') return { name: 'closedloans' };
  if (p === '/events') return { name: 'events' };
  if (p === '/locations') return { name: 'locations' };
  if (p === '/help') return { name: 'help' };
  return { name: 'notfound' };
}
